<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row align-items-center">

          <!-- Logo & Text -->
          <li class="nav-item">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <!-- Full logo - shown when expanded -->
              <span class="brand-logo full-logo">
                <brand-logo preset="navigation" />
              </span>
              <!-- Collapsed logo - shown when collapsed (icon image) -->
              <span class="brand-logo collapsed-logo">
                <brand-logo
                  variant="icon"
                  preset="compact"
                  class="collapsed-logo-img"
                />
              </span>
            </b-link>
          </li>

          <!-- Toggler Button - positioned next to logo -->
          <li class="nav-item nav-toggle ml-auto">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="nav"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import navMenuItems from '@/navigation/vertical'
import navMenuItemsSupplier from '@/navigation/vertical/supplier'
import navMenuItemsManager from '@/navigation/vertical/manager'
import navMenuItemsSales from '@/navigation/vertical/sales'
import navMenuItemsCustomerSupport from '@/navigation/vertical/customer_support'
import navMenuItemsWarehouseOperator from '@/navigation/vertical/warehouse_operator'
import navMenuItemsAdminDeveloper from '@/navigation/vertical/admin_developer'
import navMenuItemsAccountant from '@/navigation/vertical/accountant'
import navMenuItemsDeliveryMan from '@/navigation/vertical/delivery_man'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink } from 'bootstrap-vue'
import { provide, computed, ref } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import store from '@/store/store'
import { filterNavigation } from '@/utils/navigationFilter'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'ChevronRightIcon' : 'ChevronLeftIcon'))

    // App Name
    const { appName } = $themeConfig.app

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
    }
  },
  computed: {
    nav() {
      let navItems = []
      const role = store.state.user?.role || store.state.role || localStorage.getItem('role')
      if (role === 'supplier') {
        navItems = navMenuItemsSupplier
      } else if (role === 'manager') {
        navItems = navMenuItemsManager
      } else if (role === 'warehouse_operator') {
        navItems = navMenuItemsWarehouseOperator
      } else if (role === 'sales') {
        navItems = navMenuItemsSales
      } else if (role === 'customer_support') {
        navItems = navMenuItemsCustomerSupport
      } else if (role === 'admin_developer') {
        navItems = navMenuItemsAdminDeveloper
      } else if (role === 'accountant') {
        navItems = navMenuItemsAccountant
      } else if (role === 'delivery_man' || role === 'pickupeur') {
        navItems = navMenuItemsDeliveryMan
      } else if (role === 'admin' || role === 'superadmin') {
        navItems = navMenuItems
      }
      // Apply module filtering to navigation
      return filterNavigation(navItems)
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

// Modern Clean Sidebar Design - Colors driven by branding.json via CSS variables
.main-menu {
  background: var(--menu-bg, #ffffff) !important;
  box-shadow: none !important;
  border-right: 1px solid var(--menu-border-color, #f0f0f0) !important;

  .navbar-header {
    height: 70px !important;
    overflow: visible !important;
    padding: 0.75rem 1rem;
    background: var(--menu-bg, #ffffff) !important;
    border-bottom: 1px solid var(--menu-border-color, #f0f0f0);
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;

    .nav.navbar-nav {
      width: 100% !important;
      justify-content: space-between !important;
    }

    .navbar-brand {
      margin-top: 0 !important;
      display: flex !important;
      align-items: center;
      padding: 0 !important;

      // Full logo - shown when expanded
      .full-logo {
        display: block !important;
        visibility: visible !important;

        img {
          max-width: 100% !important;
          width: 140px !important;
          height: 48px !important;
          display: block !important;
          visibility: visible !important;
          filter: none !important;
        }
      }

      // Collapsed logo - hidden when expanded
      .collapsed-logo {
        display: none !important;

        .collapsed-logo-img {
          width: 38px !important;
          height: 38px !important;
          object-fit: contain;
        }
      }
    }

    .nav-toggle {
      .modern-nav-toggle {
        padding: 0.5rem !important;
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        outline: none !important;

        svg {
          color: var(--menu-text-color, #1D1D1B) !important;
          stroke: var(--menu-text-color, #1D1D1B) !important;
          background: transparent !important;
          fill: none !important;
        }

        &:hover {
          background: transparent !important;

          svg {
            color: var(--menu-toggle-hover-color, #ff6b66) !important;
            stroke: var(--menu-toggle-hover-color, #ff6b66) !important;
          }
        }

        &:focus, &:active {
          background: transparent !important;
          box-shadow: none !important;
          outline: none !important;
        }
      }
    }
  }

  // COLLAPSED STATE STYLES
  &:not(.expanded) {
    .navbar-header {
      padding: 1rem 0 !important;
      justify-content: center !important;
      text-align: center !important;
      width: 100% !important;

      .nav.navbar-nav {
        width: 100% !important;
        justify-content: center !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      .nav-item {
        width: 100% !important;
        display: flex !important;
        justify-content: center !important;
      }

      .navbar-brand {
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;

        // Hide full logo when collapsed
        .full-logo {
          display: none !important;
        }

        // Show collapsed logo when collapsed - perfectly centered
        .collapsed-logo {
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          width: 100% !important;

          .collapsed-logo-img {
            width: 38px !important;
            height: 38px !important;
          }
        }
      }

      .nav-toggle {
        display: none !important;
      }
    }

    // Center icons when collapsed - NO background colors
    .navigation {
      li {
        margin: 2px 5px !important;

        a {
          justify-content: center !important;
          padding: 12px 10px !important;
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;

          svg, i {
            margin-right: 0 !important;
          }

          .menu-title {
            display: none !important;
          }
        }

        // Active state - no background when collapsed, just keep icon visible
        &.active > a {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          border-left: none !important;

          svg, i {
            color: var(--menu-active-border-color, #ff6b66) !important;
            stroke: var(--menu-active-border-color, #ff6b66) !important;
          }
        }

        // Hover state - no background
        &:hover > a {
          background: transparent !important;

          svg, i {
            color: var(--menu-active-border-color, #ff6b66) !important;
            stroke: var(--menu-active-border-color, #ff6b66) !important;
          }
        }

        // Hide submenu arrows when collapsed
        &.has-sub > a:after {
          display: none !important;
        }
      }
    }
  }

  // EXPANDED STATE - ensure logo is visible
  &.expanded {
    .navbar-header {
      .navbar-brand {
        .full-logo {
          display: block !important;
        }

        .collapsed-logo {
          display: none !important;
        }
      }
    }
  }

  // Main navigation styling
  .navigation {
    background: var(--menu-bg, #ffffff) !important;
    padding-top: 0.5rem !important;

    // GLOBAL OVERRIDE: Remove ALL shadows from active items
    .active {
      background: var(--menu-active-bg, #ffe8e8) !important;
      box-shadow: none !important;
      border-radius: 0 !important;

      > a {
        background: var(--menu-active-bg, #ffe8e8) !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        color: var(--menu-text-color, #1D1D1B) !important;
      }
    }

    // Override submenu active items
    > li > ul .active {
      background: var(--menu-active-bg, #ffe8e8) !important;
      box-shadow: none !important;
      border-radius: 0 !important;

      > a {
        background: var(--menu-active-bg, #ffe8e8) !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        color: var(--menu-text-color, #1D1D1B) !important;
      }
    }

    // Disable all hover transforms - keep items static
    li.nav-item {
      a:hover > * {
        transform: none !important;
      }
      ul.menu-content li a:hover > * {
        transform: none !important;
      }
    }

    // Navigation header/section titles
    .navigation-header {
      color: var(--menu-section-header-color, #999999) !important;
      font-size: 0.7rem !important;
      text-transform: uppercase !important;
      letter-spacing: 0.8px !important;
      font-weight: 600 !important;
      margin-top: 1.5rem !important;
      padding-left: 1rem !important;
    }

    // All navigation items
    li {
      margin: 2px 10px !important;

      a {
        color: var(--menu-text-color, #1D1D1B) !important;
        padding: 10px 15px !important;
        border-radius: 0 !important;
        transition: background-color 0.15s ease !important;
        font-weight: 400 !important;
        font-size: 0.9rem !important;
        display: flex !important;
        align-items: center !important;
        transform: none !important;
        box-shadow: none !important;
        border-left: 3px solid transparent !important;

        // Main menu icons
        svg, i {
          color: var(--menu-text-color, #1D1D1B) !important;
          stroke: var(--menu-text-color, #1D1D1B) !important;
          width: 18px !important;
          height: 18px !important;
          margin-right: 12px !important;
          flex-shrink: 0 !important;
          transform: none !important;
        }

        // Remove circle icons from all menu items
        .feather-circle {
          display: none !important;
        }

        // Hover state - simple light background, no transform
        &:hover {
          background: var(--menu-hover-bg, #ffe8e8) !important;
          color: var(--menu-text-color, #1D1D1B) !important;
          transform: none !important;
          box-shadow: none !important;

          svg, i {
            color: var(--menu-text-color, #1D1D1B) !important;
            stroke: var(--menu-text-color, #1D1D1B) !important;
            transform: none !important;
          }

          & > * {
            transform: none !important;
          }
        }
      }

      // Active state - with left border indicator
      &.active {
        > a {
          background: var(--menu-active-bg, #ffe8e8) !important;
          box-shadow: none !important;
          color: var(--menu-text-color, #1D1D1B) !important;
          font-weight: 500 !important;
          border-left: 3px solid var(--menu-active-border-color, #ff6b66) !important;
          border-radius: 0 !important;
          margin-left: 0 !important;
          padding: 10px 15px !important;
          padding-left: 12px !important;
          transform: none !important;

          svg, i {
            color: var(--menu-text-color, #1D1D1B) !important;
            stroke: var(--menu-text-color, #1D1D1B) !important;
            transform: none !important;
          }
        }
      }

      // Open submenu state - keep simple
      &.open:not(.menu-item-closing),
      &.sidebar-group-active {
        > a {
          background: transparent !important;
          color: var(--menu-text-color, #1D1D1B) !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          transform: none !important;

          svg, i {
            color: var(--menu-text-color, #1D1D1B) !important;
            stroke: var(--menu-text-color, #1D1D1B) !important;
            transform: none !important;
          }
        }
      }

      // Submenu items - same as main items
      ul.menu-content {
        li {
          margin: 2px 10px !important;

          a {
            padding: 10px 15px !important;
            font-size: 0.9rem !important;
            color: var(--menu-text-color, #1D1D1B) !important;
            background: transparent !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            transform: none !important;
            border-left: 3px solid transparent !important;
            outline: none !important;
            border-top: none !important;
            border-bottom: none !important;
            border-right: none !important;

            // Hide the circle icon completely
            svg, i {
              display: none !important;
            }

            // No dash - keep it simple
            &::before {
              display: none !important;
            }

            &:hover {
              background: var(--menu-hover-bg, #ffe8e8) !important;
              transform: none !important;
              box-shadow: none !important;
              outline: none !important;
              border-top: none !important;
              border-bottom: none !important;
              border-right: none !important;

              & > * {
                transform: none !important;
              }
            }

            &:focus {
              outline: none !important;
              box-shadow: none !important;
            }
          }

          // Hover - keep text, no transform
          &:hover > a {
            background: var(--menu-hover-bg, #ffe8e8) !important;
            color: var(--menu-text-color, #1D1D1B) !important;
            transform: none !important;
            box-shadow: none !important;
            outline: none !important;
            border-top: none !important;
            border-bottom: none !important;
            border-right: none !important;
          }

          // Active submenu item - same style
          &.active > a {
            background: var(--menu-active-bg, #ffe8e8) !important;
            border-left: 3px solid var(--menu-active-border-color, #ff6b66) !important;
            border-radius: 0 !important;
            margin-left: 0 !important;
            padding: 10px 15px !important;
            padding-left: 12px !important;
            color: var(--menu-text-color, #1D1D1B) !important;
            font-weight: 500 !important;
            box-shadow: none !important;
            transform: none !important;
          }
        }
      }
    }
  }

  // Shadow at top when scrolled
  .shadow-bottom {
    background: linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%) !important;
  }

  // Collapse toggle icon - clean styling without background
  .modern-nav-toggle {
    color: var(--menu-text-color, #1D1D1B) !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;

    &:hover {
      color: var(--menu-toggle-hover-alt-color, #EB2322) !important;
      background: transparent !important;
    }

    .collapse-toggle-icon {
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
      fill: none !important;
      stroke: var(--menu-text-color, #1D1D1B) !important;
      color: var(--menu-text-color, #1D1D1B) !important;

      circle, path, rect, line, polyline, polygon {
        fill: none !important;
        stroke: currentColor !important;
      }

      &:hover {
        stroke: var(--menu-toggle-hover-alt-color, #EB2322) !important;
        color: var(--menu-toggle-hover-alt-color, #EB2322) !important;
        background: transparent !important;
      }
    }
  }

  // Extra override for toggle icon
  .nav-toggle .collapse-toggle-icon,
  .collapse-toggle-icon {
    background: transparent !important;
    fill: none !important;

    circle, path {
      fill: none !important;
    }
  }
}

// Override for menu-light class
.main-menu.menu-light {
  background: var(--menu-bg, #ffffff) !important;

  .navigation {
    background: var(--menu-bg, #ffffff) !important;
  }
}

// Scrollbar styling for the sidebar
.main-menu-content.scroll-area {
  &::-webkit-scrollbar {
    width: 4px !important;
  }

  &::-webkit-scrollbar-track {
    background: var(--menu-scrollbar-track, #f8f8f8) !important;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--menu-scrollbar-thumb, #dddddd) !important;
    border-radius: 4px !important;

    &:hover {
      background: var(--menu-scrollbar-thumb-hover, #cccccc) !important;
    }
  }
}

// HIGHEST PRIORITY: Collapsed menu - NO backgrounds on icons
.main-menu:not(.expanded) {
  .navigation {
    li,
    li.active,
    li:hover,
    li.nav-item,
    li.nav-item.active {
      background: transparent !important;
      box-shadow: none !important;

      > a,
      a {
        background: transparent !important;
        box-shadow: none !important;
        border: none !important;
        border-left: none !important;
      }
    }

    .active {
      background: transparent !important;

      > a {
        background: transparent !important;
      }
    }
  }
}
</style>
